import { Alert, Box, Link } from "@mui/material";
import useRoles from "../../hooks/useRoles";
import { useCurrentOrganization } from "../facility/hooks/useOrganization";
import { getBillingOrgAuthId } from "../../utils/getBillingOrgAuthId";
import { hasSubscriptionManagerRole } from "../../utils/roleUtils";
import TermsAcceptanceModal from "./TermsAcceptanceModal";
import { useTermsAcceptance } from "./hooks/useTermsAcceptance";
import useProfileModal from "../../hooks/useProfileModal";

/**
 * Prompts Subscription Managers to sign the current Terms after login,
 * including when a payment method already exists. Declining keeps the user
 * signed in and shows a banner they can use to reopen the modal.
 */
const TermsAcceptanceGate = () => {
  const roles = useRoles();
  const organization = useCurrentOrganization();
  const orgId = getBillingOrgAuthId(organization);
  const { isOpen: isProfileModalOpen } = useProfileModal();
  const shouldPrompt = hasSubscriptionManagerRole(roles);

  const {
    featureEnabled,
    termsModalOpen,
    dismissTermsPrompt,
    promptTermsAcceptance,
    handleAccepted,
    currentVersion,
    needsAcceptance,
  } = useTermsAcceptance(shouldPrompt ? orgId : undefined, {
    autoPrompt: shouldPrompt && !isProfileModalOpen,
  });

  if (!featureEnabled || !shouldPrompt || !orgId || isProfileModalOpen) {
    return null;
  }

  return (
    <>
      {needsAcceptance && !termsModalOpen ? (
        <Box sx={{ mb: 2 }} data-testid="terms-acceptance-banner">
          <Alert severity="warning" sx={{ borderRadius: 2 }}>
            You have not accepted the current Terms & Conditions. As
            Subscription Manager, you are responsible for keeping the platform
            available for your organization. Until you review and accept,
            automatic payments may not process, which can restrict features or
            remove access for your entire organization.{" "}
            <Link
              component="button"
              type="button"
              underline="always"
              onClick={promptTermsAcceptance}
              data-testid="terms-acceptance-banner-link"
              sx={{
                verticalAlign: "baseline",
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              Review and accept Terms & Conditions
            </Link>
          </Alert>
        </Box>
      ) : null}
      <TermsAcceptanceModal
        open={termsModalOpen}
        onClose={dismissTermsPrompt}
        orgId={orgId}
        terms={currentVersion}
        onAccepted={handleAccepted}
      />
    </>
  );
};

export default TermsAcceptanceGate;
